import { ROTATION_SNAP_DEGREES, clampArrowHeadSize, normalizeRotation, type Annotation, type AnnoColor } from '@jobos/shared';

// Pure helpers for building/transforming annotations in normalized 0–1 space.
// The DOM/SVG event handling lives in AnnotationLayer; this is the testable core.

// Clamp to [0,1] and round to 4 decimals — enough precision for annotations and
// keeps the stored JSON free of floating-point noise.
export const clamp01 = (v: number): number => Math.round(Math.max(0, Math.min(1, v)) * 10000) / 10000;

const newId = () => crypto.randomUUID();

// headSize/size/color are omitted when not given, so an annotation drawn at the
// render defaults stays free of stored values (and follows the defaults if they
// ever move).
export function createArrow(x1: number, y1: number, x2: number, y2: number, headSize?: number, color?: AnnoColor): Annotation {
  return {
    id: newId(),
    type: 'arrow',
    x1: clamp01(x1),
    y1: clamp01(y1),
    x2: clamp01(x2),
    y2: clamp01(y2),
    ...(headSize != null ? { headSize: clampArrowHeadSize(headSize) } : {}),
    ...(color != null ? { color } : {}),
  };
}

// Plain segment — an arrow with nothing drawn on its ends. Its size is its
// stroke weight, stored on the same scale as an arrow's head size.
export function createLine(x1: number, y1: number, x2: number, y2: number, size?: number, color?: AnnoColor): Annotation {
  return {
    id: newId(),
    type: 'line',
    x1: clamp01(x1),
    y1: clamp01(y1),
    x2: clamp01(x2),
    y2: clamp01(y2),
    ...(size != null ? { size: clampArrowHeadSize(size) } : {}),
    ...(color != null ? { color } : {}),
  };
}

// Measurement line: arrow heads plus perpendicular tick caps, sized by the same
// headSize an arrow uses.
export function createDimension(x1: number, y1: number, x2: number, y2: number, headSize?: number, color?: AnnoColor): Annotation {
  return {
    id: newId(),
    type: 'dimension',
    x1: clamp01(x1),
    y1: clamp01(y1),
    x2: clamp01(x2),
    y2: clamp01(y2),
    ...(headSize != null ? { headSize: clampArrowHeadSize(headSize) } : {}),
    ...(color != null ? { color } : {}),
  };
}

// Built from a drag bounding box (two corners) → center + half-extents.
export function createCircle(x1: number, y1: number, x2: number, y2: number, color?: AnnoColor): Annotation {
  return {
    id: newId(),
    type: 'circle',
    x: clamp01((x1 + x2) / 2),
    y: clamp01((y1 + y2) / 2),
    rx: clamp01(Math.abs(x2 - x1) / 2),
    ry: clamp01(Math.abs(y2 - y1) / 2),
    ...(color != null ? { color } : {}),
  };
}

// Built from a drag bounding box (two corners) → top-left corner + extents, so a
// drag in any direction produces the same rectangle.
export function createRect(x1: number, y1: number, x2: number, y2: number, color?: AnnoColor): Annotation {
  return {
    id: newId(),
    type: 'rect',
    x: clamp01(Math.min(x1, x2)),
    y: clamp01(Math.min(y1, y2)),
    w: clamp01(Math.abs(x2 - x1)),
    h: clamp01(Math.abs(y2 - y1)),
    ...(color != null ? { color } : {}),
  };
}

export function createText(x: number, y: number, text: string, size?: number, color?: AnnoColor): Annotation {
  return {
    id: newId(),
    type: 'text',
    x: clamp01(x),
    y: clamp01(y),
    text,
    ...(size != null ? { size } : {}),
    ...(color != null ? { color } : {}),
  };
}

// Size edits from the inspector. Both are no-ops on the wrong annotation type,
// so the caller can hand over whatever is selected.
export function setTextSize(annotations: Annotation[], id: string, size: number): Annotation[] {
  return annotations.map((a) => (a.id === id && a.type === 'text' ? { ...a, size } : a));
}

// One size control for every segment type — it lands on `headSize` where there
// is a head to grow, and on the plain line's `size` (its stroke weight) where
// there isn't.
export function setSegmentSize(annotations: Annotation[], id: string, size: number): Annotation[] {
  const clamped = clampArrowHeadSize(size);
  return annotations.map((a) => {
    if (a.id !== id) return a;
    if (a.type === 'arrow' || a.type === 'dimension') return { ...a, headSize: clamped };
    if (a.type === 'line') return { ...a, size: clamped };
    return a;
  });
}

/** The size a selected segment is currently drawn at, whichever field holds it. */
export function segmentSize(a: Annotation): number | undefined {
  if (a.type === 'arrow' || a.type === 'dimension') return a.headSize;
  if (a.type === 'line') return a.size;
  return undefined;
}

// Color applies to every annotation type, so unlike the size edits this one is
// never a no-op on the selected annotation.
export function setAnnotationColor(annotations: Annotation[], id: string, color: AnnoColor): Annotation[] {
  return annotations.map((a) => (a.id === id ? { ...a, color } : a));
}

// ── Tilt ──────────────────────────────────────────────────────────────────
// Circles and rectangles can be rotated about their own center. Everything
// below works in normalized 0–1 space; the angle is the same number in viewBox
// space (the mapping between them is a uniform ×100), so it can be handed to
// the renderers untouched.

/** The box-drawn shapes — the only ones that carry a tilt. */
export type BoxAnnotation = Extract<Annotation, { type: 'circle' | 'rect' }>;

const isBox = (a: Annotation): a is BoxAnnotation => a.type === 'circle' || a.type === 'rect';

type Pt = { x: number; y: number };

const centerOf = (a: BoxAnnotation): Pt =>
  a.type === 'circle' ? { x: a.x, y: a.y } : { x: a.x + a.w / 2, y: a.y + a.h / 2 };

/** Rotate `p` by `deg` (clockwise, screen axes) about `c`. */
export function rotatePoint(p: Pt, c: Pt, deg: number): Pt {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - c.x;
  const dy = p.y - c.y;
  return { x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos };
}

/**
 * The corner a resize anchors on — the shape's local top-left — as it actually
 * sits on the page once the tilt is applied. Resizing pins THIS point: without
 * that, dragging the grip on a tilted shape walks it across the photo.
 */
export function rotatedCorner(a: BoxAnnotation): Pt {
  const c = centerOf(a);
  const local = a.type === 'circle' ? { x: a.x - a.rx, y: a.y - a.ry } : { x: a.x, y: a.y };
  return rotatePoint(local, c, a.rotation ?? 0);
}

/** Set (or clear) a shape's tilt. A level shape stores no `rotation` at all, so
 *  rotating back to 0 leaves the document exactly as it was before. */
export function setAnnotationRotation(annotations: Annotation[], id: string, deg: number): Annotation[] {
  return annotations.map((a) => {
    if (a.id !== id || !isBox(a)) return a;
    const rotation = normalizeRotation(deg);
    if (rotation === 0) {
      const { rotation: _drop, ...rest } = a;
      return rest as Annotation;
    }
    return { ...a, rotation };
  });
}

/**
 * The angle the rotate grip is being dragged to: measured from straight up
 * (the grip's resting place), clockwise. With `snap` on it lands on the nearest
 * 15° stop, so 45° and 90° are exact rather than eyeballed. A pointer sitting
 * exactly on the center has no direction, so the current angle is kept.
 */
export function rotationFromPointer(a: Annotation, p: Pt, snap: boolean): number {
  if (!isBox(a)) return 0;
  const c = centerOf(a);
  const dx = p.x - c.x;
  const dy = p.y - c.y;
  if (dx === 0 && dy === 0) return normalizeRotation(a.rotation ?? 0);
  const deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
  return normalizeRotation(snap ? Math.round(deg / ROTATION_SNAP_DEGREES) * ROTATION_SNAP_DEGREES : deg);
}

/**
 * Reshape a box-drawn annotation by dragging its resize grip to `p`, keeping
 * the opposite corner pinned where it is on the page. The pointer is measured
 * in the shape's own (untilted) frame, so a tilted shape grows along its own
 * axes; dragging past the anchor flips the box rather than inverting it.
 */
export function resizeBoxAnnotation(a: Annotation, p: Pt): Annotation {
  if (!isBox(a)) return a;
  const deg = a.rotation ?? 0;
  const anchor = rotatedCorner(a);
  // Pointer in the shape's local frame, relative to the pinned corner.
  const q = rotatePoint(p, anchor, -deg);
  const w = Math.abs(q.x - anchor.x);
  const h = Math.abs(q.y - anchor.y);
  // The new center sits half way from the anchor to the pointer — in the local
  // frame, then rotated back out to the page.
  const center = rotatePoint({ x: (anchor.x + q.x) / 2, y: (anchor.y + q.y) / 2 }, anchor, deg);
  if (a.type === 'circle') {
    return { ...a, x: clamp01(center.x), y: clamp01(center.y), rx: clamp01(w / 2), ry: clamp01(h / 2) };
  }
  return { ...a, x: clamp01(center.x - w / 2), y: clamp01(center.y - h / 2), w: clamp01(w), h: clamp01(h) };
}

export function translateAnnotation(a: Annotation, dx: number, dy: number): Annotation {
  switch (a.type) {
    case 'arrow':
    case 'line':
    case 'dimension':
      return { ...a, x1: clamp01(a.x1 + dx), y1: clamp01(a.y1 + dy), x2: clamp01(a.x2 + dx), y2: clamp01(a.y2 + dy) };
    case 'circle':
    case 'rect':
      return { ...a, x: clamp01(a.x + dx), y: clamp01(a.y + dy) };
    case 'text':
      return { ...a, x: clamp01(a.x + dx), y: clamp01(a.y + dy) };
  }
}

// How far a paste lands from what it was copied from, in normalized units — far
// enough that the copy is visibly its own annotation rather than sitting exactly
// on the original. Only applied when pasting back onto the same page.
export const ANNOTATION_PASTE_OFFSET = 0.02;

// A copy of `a` at a new position, with its own id. Everything else — the note's
// text and size, an arrow's head size, a circle's radii — carries across
// untouched, so a pasted annotation renders identically to the one it came from.
export function cloneAnnotation(a: Annotation, dx: number, dy: number): Annotation {
  return { ...translateAnnotation(a, dx, dy), id: newId() };
}
